// Iter163 — Cache-buster ``?v=v2`` no thumb otimizado. Bumpamos a
// resolução do WebP renderizado de 480px → 960px, mas as URLs
// antigas estavam cacheadas em navegadores e no edge Cloudflare
// por 1 ano (Cache-Control: immutable). Sem alterar a URL, o
// visitante continua vendo a versão embaçada mesmo após o deploy.
// O param ``v`` é ignorado pelo backend (o filename no disco é o
// mesmo) mas força um miss no CDN → puxa a nova versão.
const OPTIMIZED_THUMB_VERSION = "v2";

// Build-time substitution by CRA. When the SPA and API live on different
// origins (e.g. preview environments), this is set; in production with a
// shared domain the request goes through the same origin via ingress.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Pick a safe YouTube thumbnail URL.
 *
 * YouTube only generates ``maxresdefault.jpg`` (1280×720) **after** the
 * upload finishes processing, and even when the API advertises it the CDN
 * frequently serves a grey "video unavailable" placeholder for the first
 * minutes. The browser receives HTTP 200 with that placeholder, so an
 * ``onError`` fallback never fires — the user simply sees a blank cover
 * across every listing on the site.
 *
 * The fix is to rewrite stored ``/maxresdefault.jpg`` URLs to
 * ``/hqdefault.jpg`` (480×360, generated instantly the moment the video is
 * published, never goes missing). Slightly lower resolution, infinitely
 * more reliable. We do this client-side so legacy DB rows are healed
 * without waiting for an admin to run the backfill.
 *
 * ``options.quality`` lets callers pick the right tier:
 *   - "featured" → /api/thumb/{id}.webp (locally optimized WebP, ~14 KB,
 *     long-cached at the edge). Use for above-the-fold home cards.
 *   - "list" (default) → mqdefault.jpg from YouTube's CDN (~9 KB, served
 *     globally). Use for shelves, archives, search results, anything
 *     below-the-fold.
 *   - "high" → hqdefault.jpg (480×360, ~30 KB). Use only on the dedicated
 *     episode page where the thumbnail is the focal art before playback.
 */
export function safeThumb(stored, youtubeId, options = {}) {
  const quality = options.quality || "list";

  // Custom uploads stored in our own backend always win — admins overrode
  // the YouTube cover for a reason, and we shouldn't second-guess them
  // regardless of ``quality``.
  if (stored && stored.startsWith("/api/")) {
    return BACKEND_URL ? `${BACKEND_URL}${stored}` : stored;
  }

  // Featured: route through our optimized WebP endpoint when we have a
  // YouTube id to work with. Falls back to the original stored URL when
  // the backend can't be reached (preview without API).
  if (quality === "featured" && youtubeId) {
    const base = BACKEND_URL
      ? `${BACKEND_URL}/api/thumb/${youtubeId}.webp`
      : `/api/thumb/${youtubeId}.webp`;
    return `${base}?v=${OPTIMIZED_THUMB_VERSION}`;
  }

  // Iter163 — Below-the-fold cards: em telas grandes (desktop / retina)
  // o ``mqdefault.jpg`` (320×180) ficava borrado quando o card
  // renderizava a 400-600px. Trocamos para ``hqdefault.jpg`` (480×360,
  // ~30KB) — ainda leve o suficiente para não impactar LCP, mas
  // aguenta upscale sem pixelar no editor's Macbook 16" 3072×1920.
  if (quality === "list" && youtubeId) {
    return `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
  }

  // "high" tier or anywhere we still have a stored URL: keep the legacy
  // safe-rewrite behaviour (maxresdefault → hqdefault).
  if (!stored) {
    return youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : "";
  }
  if (stored.includes("/maxresdefault.")) {
    return stored.replace("/maxresdefault.", "/hqdefault.");
  }
  return stored;
}
